import { css } from "@emotion/react";
import type { SearchHit } from "./algolia";
import SearchIcon from "../../icons/open-in-new.svg?react";
import { renderHighlightedText } from "./utils";
import { useAppContext } from "../../contexts/AppContext";
import { usePublicationsContext } from "../../contexts/WithPublications/PublicationsContext";
import { useMemo } from "react";

const SearchResultItem = ({ searchResult }: { searchResult: SearchHit }) => {
  const { searchValue } = useAppContext();
  const publications = usePublicationsContext();

  const publication = useMemo(
    () =>
      publications?.find((pub) => pub.filename === searchResult.publicationId),
    [publications, searchResult.publicationId],
  );

  const url = `${window.compassUrl ?? import.meta.env.VITE_COMPASS_URL}/pub/${searchResult.publicationId}#${searchResult.objectID}`;
  if (!publication) {
    return null;
  }
  return (
    <div
      css={({ colours }) => css`
        display: flex;
        margin: 13px 12px 16px;
        padding: 6px;
        border-radius: 12px;
        box-shadow:
          0 1px 4px 0 rgba(9, 9, 11, 0.08),
          0 0 12px -6px ${colours.uIGrey.light._60},
          inset 0 0 4px 2px ${colours.uIGrey.light._10},
          inset 0 0 0 0.5px ${colours.uIGrey.light._5},
          inset 0 -17px 10px -1px ${colours.uIGrey.light._20};
        background-color: ${colours.uIGrey.light._5};
      `}
    >
      <div
        css={css`
          width: 76px;
          height: 108px;
          margin: 0 6px 0 0;
          padding: 4px;
          border-radius: 6px;
          -webkit-backdrop-filter: blur(12px);
          backdrop-filter: blur(12px);
          box-shadow:
            inset 0 0 2px 1px rgba(181, 184, 196, 0.2),
            inset 0 0 0 0.3px rgba(147, 150, 169, 0.2);
          background-color: rgba(164, 166, 182, 0.2);
        `}
      >
        <img
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 4px;
          `}
          src={publication.imageObjectUrl}
          alt={publication.title}
        />
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          min-width: 0;
        `}
      >
        <div
          css={({ colours }) => css`
            width: 598px;
            height: 86px;
            margin: 0 0 4px;
            padding: 4px;
            border-radius: 6px;
            box-shadow:
              0 0 4px 0 ${colours.uIGrey.light._20},
              inset 0 0 2px 1px #fff,
              inset 0 0 0 0.3px ${colours.uIGrey.light._15};
            background-color: ${colours.uIGrey.light._10};
          `}
        >
          <div
            css={({ colours }) => css`
              width: 590px;
              height: 78px;
              padding: 4px 4px 4px 12px;
              border-radius: 2px;
              box-shadow:
                0 0 12px -6px ${colours.accent.light._50},
                inset 0 0 4px 2px ${colours.accent.light._40},
                inset 0 0 0 0.5px ${colours.accent.light._10},
                inset 0 -6px 12px -2px #fff;
              background-color: ${colours.accent.light._20};

              .search-highlight {
                background-color: rgba(181, 184, 196, 0.3);
                font-style: normal;
                font-weight: 700;
              }
            `}
          >
            {renderHighlightedText(
              searchResult._snippetResult.content?.value || "",
              searchResult.publicationId,
              searchResult.publicationId,
              searchValue,
            )}
          </div>
        </div>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              gap: 4px;
              text-overflow: ellipsis;
              overflow: hidden;
              white-space: nowrap;
              min-width: 0;
              flex-grow: 1;
              justify-content: flex-start;
              padding-right: 8px;
            `}
          >
            <span
              css={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                min-width: 0;
                flex-shrink: 1;
              `}
            >
              {searchResult.chapterTitle}
            </span>
            <span
              css={css`
                flex-shrink: 0;
                white-space: nowrap;
              `}
            >
              <span>{"● "}</span>
              {publication.title}
            </span>
          </div>
          <a
            css={({ colours }) => css`
              display: flex;
              align-items: center;
              flex-shrink: 0;
              padding: 2px 6px;
              border: none;
              gap: 2px;
              opacity: 1;
              border-radius: 6px;
              height: 16px;
              box-shadow:
                0px 0px 4px 2px rgba(250, 250, 251, 1) inset,
                0px 0px 0px 0.5px rgba(255, 255, 255, 1) inset,
                0px -17px 10px -1px rgba(234, 234, 238, 1) inset,
                0px 1px 4px rgba(9, 9, 11, 0.08),
                0px 0px 12px -6px rgba(147, 150, 169, 1);
              opacity: 1;
              background-color: rgba(244, 244, 246, 1);
              color: ${colours.uIGrey.dark._30};
              font-size: 12px;
              font-weight: 600;
              letter-spacing: 0px;
            `}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              window.top?.open(url, "_blank");
            }}
          >
            View in Compass <SearchIcon aria-label="Search Icon" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default SearchResultItem;
